import React from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  IconButton,
  Alert,
  Divider,
  InputAdornment,
  Fade,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  Receipt as ReceiptIcon,
  CalendarToday as CalendarIcon,
  Assignment as AssignmentIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AttachFile as AttachFileIcon,
  PictureAsPdf as PdfIcon
} from '@mui/icons-material';
import useManualInvoiceForm from './hooks/useManualInvoiceForm';
import { styles } from './styles/manualInvoiceFormStyles';

const ManualInvoiceForm = ({ onSubmit, onCancel, isSubmitting }) => {
  const {
    formData,
    pdfFile,
    errors,
    handleChange,
    handleFileChange,
    handleRemoveFile,
    addItem,
    removeItem,
    updateItem,
    handleSubmit
  } = useManualInvoiceForm(onSubmit);

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) handleFileChange(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <Fade in={true} timeout={300}>
      <Box sx={styles.container}>
        <Box sx={styles.header}>
          <Box sx={styles.iconWrapper}>
            <ReceiptIcon sx={styles.icon} />
          </Box>
          <Typography variant="h6" sx={styles.title}>
            הזנת חשבונית ידנית
          </Typography>
          <Typography variant="body2" sx={styles.subtitle}>
            מלאי את הפרטים הבאים ליצירת חשבונית חדשה במערכת
          </Typography>
        </Box>

        <Alert severity="info" icon={false} sx={styles.infoAlert}>
          <Typography variant="body2" sx={styles.infoText}>
            💡 החשבונית תעבור את אותו תהליך אישור כמו חשבונית שהועלתה מקובץ
          </Typography>
        </Alert>

        <Box component="form" onSubmit={handleSubmit}>
          <Paper elevation={0} sx={styles.section}>
            <Typography variant="subtitle2" sx={styles.sectionTitle}>
              <PersonIcon sx={styles.sectionIcon} />
              פרטי ספק
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="שם ספק"
                  value={formData.supplierName}
                  onChange={handleChange('supplierName')}
                  error={!!errors.supplierName}
                  helperText={errors.supplierName}
                  disabled={isSubmitting}
                  required
                  size="small"
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="מספר ספק"
                  value={formData.supplierNumber}
                  onChange={handleChange('supplierNumber')}
                  disabled={isSubmitting}
                  size="small"
                  sx={styles.input}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} sx={styles.section}>
            <Typography variant="subtitle2" sx={styles.sectionTitle}>
              <BusinessIcon sx={styles.sectionIcon} />
              פרטי חברה מזמינה
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={8}>
                <TextField
                  fullWidth
                  label="שם חברה"
                  value={formData.companyName}
                  onChange={handleChange('companyName')}
                  error={!!errors.companyName}
                  helperText={errors.companyName}
                  disabled={isSubmitting}
                  required
                  size="small"
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="ח.פ חברה"
                  value={formData.companyId}
                  onChange={handleChange('companyId')}
                  error={!!errors.companyId}
                  helperText={errors.companyId}
                  inputProps={{ maxLength: 9 }}
                  disabled={isSubmitting}
                  size="small"
                  sx={styles.input}
                />
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} sx={styles.section}>
            <Typography variant="subtitle2" sx={styles.sectionTitle}>
              <ReceiptIcon sx={styles.sectionIcon} />
              פרטי חשבונית
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="מספר חשבונית"
                  value={formData.invoiceNumber}
                  onChange={handleChange('invoiceNumber')}
                  error={!!errors.invoiceNumber}
                  helperText={errors.invoiceNumber}
                  disabled={isSubmitting}
                  required
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <ReceiptIcon sx={styles.inputIcon} />
                      </InputAdornment>
                    )
                  }}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="תאריך חשבונית"
                  placeholder="dd/mm/yyyy"
                  value={formData.invoiceDate}
                  onChange={handleChange('invoiceDate')}
                  error={!!errors.invoiceDate}
                  helperText={errors.invoiceDate || 'פורמט: dd/mm/yyyy'}
                  disabled={isSubmitting}
                  required
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <CalendarIcon sx={styles.inputIcon} />
                      </InputAdornment>
                    )
                  }}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="מספר הקצאה"
                  value={formData.allocationNumber}
                  onChange={handleChange('allocationNumber')}
                  disabled={isSubmitting}
                  size="small"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AssignmentIcon sx={styles.inputIcon} />
                      </InputAdornment>
                    )
                  }}
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  select
                  label="מטבע"
                  value={formData.currencyCode}
                  onChange={handleChange('currencyCode')}
                  disabled={isSubmitting}
                  size="small"
                  SelectProps={{ native: true }}
                  sx={styles.input}
                >
                  <option value="ILS">₪ שקל (ILS)</option>
                  <option value="USD">$ דולר (USD)</option>
                  <option value="EUR">€ יורו (EUR)</option>
                </TextField>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={0} sx={styles.section}>
            <Typography variant="subtitle2" sx={styles.sectionTitle}>
              <AttachFileIcon sx={styles.sectionIcon} />
              צרף קובץ PDF (אופציונלי)
            </Typography>

            {!pdfFile ? (
              <Box
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                sx={styles.uploadBox}
              >
                <input
                  type="file"
                  accept="application/pdf"
                  onChange={(e) => handleFileChange(e.target.files[0])}
                  style={{ display: 'none' }}
                  id="pdf-upload"
                  disabled={isSubmitting}
                />
                <label htmlFor="pdf-upload" style={{ cursor: 'pointer', width: '100%' }}>
                  <Box sx={{ textAlign: 'center' }}>
                    <PdfIcon sx={{ fontSize: 48, color: '#94a3b8', mb: 1 }} />
                    <Typography variant="body2" color="text.secondary">
                      גרור קובץ PDF לכאן או לחץ לבחירה
                    </Typography>
                  </Box>
                </label>
              </Box>
            ) : (
              <Box sx={styles.filePreview}>
                <PdfIcon sx={{ fontSize: 32, color: '#ef4444', mr: 2 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography variant="body2" sx={{ fontWeight: 500 }}>
                    {pdfFile.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {(pdfFile.size / 1024 / 1024).toFixed(2)} MB
                  </Typography>
                </Box>
                <IconButton
                  size="small"
                  onClick={handleRemoveFile}
                  disabled={isSubmitting}
                  sx={styles.deleteIconButton}
                >
                  <DeleteIcon />
                </IconButton>
              </Box>
            )}
            {errors.pdfFile && (
              <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                {errors.pdfFile}
              </Typography>
            )}
          </Paper>

          <Paper elevation={0} sx={styles.section}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="subtitle2" sx={styles.sectionTitle}>
                <ReceiptIcon sx={styles.sectionIcon} />
                שורות חשבונית
              </Typography>
              <Button
                variant="outlined"
                size="small"
                startIcon={<AddIcon />}
                onClick={addItem}
                disabled={isSubmitting}
                sx={styles.addButton}
              >
                הוסף שורה
              </Button>
            </Box>

            {errors.items && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {errors.items}
              </Alert>
            )}

            <TableContainer sx={styles.tableContainer}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>קוד</TableCell>
                    <TableCell>תיאור</TableCell>
                    <TableCell align="center">כמות</TableCell>
                    <TableCell align="center">מחיר יחידה</TableCell>
                    <TableCell align="center">שער חליפין</TableCell>
                    <TableCell align="center">סה"כ</TableCell>
                    <TableCell align="center" width="50px"></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {formData.items.map((item, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={item.code}
                          onChange={(e) => updateItem(index, 'code', e.target.value)}
                          error={!!errors[`item_${index}_code`]}
                          disabled={isSubmitting}
                          placeholder="קוד"
                          sx={{ minWidth: '100px' }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          value={item.description}
                          onChange={(e) => updateItem(index, 'description', e.target.value)}
                          error={!!errors[`item_${index}_description`]}
                          disabled={isSubmitting}
                          placeholder="תיאור פריט"
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={item.quantity}
                          onChange={(e) => updateItem(index, 'quantity', e.target.value)}
                          error={!!errors[`item_${index}_quantity`]}
                          disabled={isSubmitting}
                          inputProps={{ step: '0.01', min: '0' }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={item.unitPrice}
                          onChange={(e) => updateItem(index, 'unitPrice', e.target.value)}
                          error={!!errors[`item_${index}_unitPrice`]}
                          disabled={isSubmitting}
                          inputProps={{ step: '0.01', min: '0' }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={item.exchangeRate}
                          onChange={(e) => updateItem(index, 'exchangeRate', e.target.value)}
                          disabled={isSubmitting}
                          inputProps={{ step: '0.0001', min: '0' }}
                        />
                      </TableCell>
                      <TableCell>
                        <TextField
                          fullWidth
                          size="small"
                          type="number"
                          value={item.total}
                          onChange={(e) => updateItem(index, 'total', e.target.value)}
                          error={!!errors[`item_${index}_total`]}
                          disabled={isSubmitting}
                          inputProps={{ step: '0.01', min: '0' }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          size="small"
                          onClick={() => removeItem(index)}
                          disabled={isSubmitting}
                          sx={styles.deleteIconButton}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>

          <Paper elevation={0} sx={styles.section}>
            <Typography variant="subtitle2" sx={styles.sectionTitle}>
              סיכום סכומים
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="סה״כ לפני מע״מ"
                  type="number"
                  value={formData.subtotal}
                  onChange={handleChange('subtotal')}
                  error={!!errors.subtotal}
                  helperText={errors.subtotal}
                  inputProps={{ step: '0.01', min: '0' }}
                  disabled={isSubmitting}
                  required
                  size="small"
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="מע״מ"
                  type="number"
                  value={formData.vatAmount}
                  onChange={handleChange('vatAmount')}
                  error={!!errors.vatAmount}
                  helperText={errors.vatAmount}
                  inputProps={{ step: '0.01', min: '0' }}
                  disabled={isSubmitting}
                  required
                  size="small"
                  sx={styles.input}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="סה״כ כולל מע״מ"
                  type="number"
                  value={formData.total}
                  onChange={handleChange('total')}
                  error={!!errors.total}
                  helperText={errors.total}
                  inputProps={{ step: '0.01', min: '0' }}
                  disabled={isSubmitting}
                  required
                  size="small"
                  sx={styles.input}
                />
              </Grid>
            </Grid>
          </Paper>

          <Box sx={styles.actions}>
            <Button
              variant="outlined"
              onClick={onCancel}
              disabled={isSubmitting}
              sx={styles.cancelButton}
            >
              ביטול
            </Button>
            <Button
              type="submit"
              variant="contained"
              disabled={isSubmitting}
              sx={styles.submitButton}
              startIcon={isSubmitting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : null}
            >
              {isSubmitting ? 'שומר ומעבד...' : 'שמור והמשך לעיבוד'}
            </Button>
          </Box>
        </Box>
      </Box>
    </Fade>
  );
};

export default ManualInvoiceForm;